import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { dataService } from '@/services/dataService';
import { Contact, KanbanStage, CRMStats } from '@/types/crm';
import { CRMHeader } from '@/components/crm/CRMHeader';
import { KanbanColumn } from '@/components/crm/KanbanColumn';
import { StatusIndicator } from '@/components/crm/StatusIndicator';
import { LoadingState } from '@/components/crm/LoadingState';
import { ErrorState } from '@/components/crm/ErrorState';
import Settings from '@/pages/Settings';

const DEFAULT_STAGES: KanbanStage[] = [
  { id: 'triagem_inicial', title: 'Triagem Inicial', icon: '📝', color: '#6B46C1' },
  { id: 'negociacao', title: 'Negociação', icon: '💬', color: '#7C3AED' },
  { id: 'customizacao', title: 'Customização', icon: '🎨', color: '#3B82F6' },
  { id: 'interesse_identificado', title: 'Interesse Identificado', icon: '✨', color: '#60A5FA' },
  { id: 'confirmacao_pagamento', title: 'Confirmação Pagamento', icon: '💳', color: '#10b981' }
];

const STAGE_COLORS = ['#6B46C1', '#7C3AED', '#3B82F6', '#60A5FA', '#10b981', '#f59e0b', '#ef4444'];

const Index = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading, signOut } = useAuth();
  
  const [data, setData] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [stats, setStats] = useState<CRMStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Redirect based on authentication status
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (isAdmin) {
        navigate('/admin');
      } else {
        // Regular users go to ClientCRM
        navigate('/crm');
      }
    }
  }, [user, isAdmin, authLoading, navigate]);

  useEffect(() => {
    dataService.startPolling();

    const unsubscribe = dataService.subscribe((newData) => {
      setData(newData);
      setLoading(false);
      setStats(dataService.getStats());
    });

    return () => {
      unsubscribe();
      dataService.stopPolling();
    };
  }, []);

  // Filtros
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter(item =>
      (item.nome_cliente || '').toLowerCase().includes(searchLower) ||
      (item.telefone || '').includes(searchLower) ||
      (item.categoria_produto || '').toLowerCase().includes(searchLower) ||
      (item.item_pedido || '').toLowerCase().includes(searchLower)
    );
  }, [data, searchTerm]);

  // Etapas do Kanban
  const stages = useMemo((): KanbanStage[] => {
    const uniqueStages = [...new Set(data.map(item => item.etapa_atual).filter(Boolean))];

    if (uniqueStages.length === 0) {
      return DEFAULT_STAGES;
    }

    return uniqueStages.map((stage, index) => ({
      id: stage!.toLowerCase().replace(/\s+/g, '_'),
      title: stage!,
      icon: '📊',
      color: STAGE_COLORS[index % STAGE_COLORS.length]
    }));
  }, [data]);

  // Agrupa leads por etapa
  const leadsByStage = useMemo(() => {
    const grouped: Record<string, Contact[]> = {};
    stages.forEach(stage => {
      grouped[stage.id] = filteredData.filter(
        lead => (lead.etapa_atual || '').toLowerCase().replace(/\s+/g, '_') === stage.id
      );
    });
    return grouped;
  }, [filteredData, stages]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await dataService.fetchData();
    setTimeout(() => setRefreshing(false), 500);
  };

  if (authLoading) {
    return <LoadingState />;
  }

  if (loading && data.length === 0) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={handleRefresh} />;
  }

  return (
    <div className="min-h-screen bg-galaxia">
      <CRMHeader
        stats={stats}
        onSearch={setSearchTerm}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        userEmail={user?.email}
        onLogout={signOut}
        onOpenSettings={() => setShowSettings(true)}
      />

      <main className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="flex gap-5 overflow-x-auto pb-5">
          {stages.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              leads={leadsByStage[stage.id] || []}
            />
          ))}
        </div>
      </main>

      <StatusIndicator />
      
      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

export default Index;
